import { PhotoIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { MonitoringBody } from './MonitoringBody';
import type { MonitoringMorphing } from './MonitoringMedia';
import { ScanTimeline } from './timeline';
import type { MediaView } from './MediaToggleBar';
import type { DmScanPhoto, DmScanSummary } from './types';

export type { MonitoringMorphing };

export interface MonitoringPanelProps {
    scans: DmScanSummary[] | null | undefined;
    scansLoading?: boolean;
    selectedScanId: number | null;
    onSelectScan: (scanId: number) => void;
    selectedScan: DmScanSummary | null;
    photos: DmScanPhoto[] | null | undefined;
    photosLoading?: boolean;
    totalAligners?: number | null;
    /** Undefined hides the status in the scan header. */
    monitoringStatus?: string | null;
    currentAligner?: number | null;
    /** Omit to hide the Photos / Smile Morph toggle entirely. */
    morphing?: MonitoringMorphing | null;
    /** Optional column rendered beside the media (indices, observations…). */
    sidePanel?: ReactNode;
    /** Side-by-side scan comparison; omit to hide the Compare toggle. */
    compareView?: ReactNode;
    /** Hide the date-scaled scan timeline (e.g. surfaces pinned to one scan). */
    showScanSelector?: boolean;
    /** Adds the expand control that fills the viewport with the whole panel. */
    allowFullscreen?: boolean;
    /**
     * False when DM issues no aligner-change instruction for this monitoring, so
     * the go/no-go affordances are dropped rather than drawn empty. See `DmScanList`.
     */
    tracksAligners?: boolean;
    formatScanDate?: (value: string) => string;
    emptyState?: ReactNode;
    className?: string;
    /** Host controls in the scan header (before the Details toggle) and above the empty state. */
    headerActions?: ReactNode;
    /** Rendered right after the Details toggle, and above the empty state. */
    detailsMenu?: ReactNode;
}

function DefaultEmptyState() {
    return (
        <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400 py-12">
            <PhotoIcon className="w-10 h-10 opacity-30" />
            <span className="text-sm font-semibold font-inter text-gray-600">No scans yet</span>
            <span className="text-xs font-inter text-gray-400 text-center max-w-[280px]">
                Scans will appear here once the patient submits their first Dental Monitoring scan.
            </span>
        </div>
    );
}

function PanelShell({
    isFullscreen,
    className,
    children
}: {
    isFullscreen: boolean;
    className: string;
    children: ReactNode;
}) {
    return (
        <div
            className={
                isFullscreen
                    ? 'fixed inset-0 z-50 bg-white p-4 flex flex-col gap-2'
                    : `flex-1 min-h-0 flex flex-col gap-2 ${className}`
            }
        >
            {children}
        </div>
    );
}

function PanelEmptyState({
    emptyState,
    className,
    headerActions,
    detailsMenu
}: Pick<MonitoringPanelProps, 'emptyState' | 'headerActions' | 'detailsMenu'> & {
    className: string;
}) {
    const empty = emptyState ?? <DefaultEmptyState />;
    if (!headerActions && !detailsMenu) return <>{empty}</>;
    return (
        <div className={`flex-1 min-h-0 flex flex-col ${className}`}>
            <div className="flex justify-end items-center gap-1.5">
                {headerActions}
                {detailsMenu}
            </div>
            {empty}
        </div>
    );
}

function useFullscreen(hasScans: boolean) {
    const [isFullscreen, setIsFullscreen] = useState(false);

    // The overlay is unreachable without scans, so it must not survive the swap.
    useEffect(() => {
        if (!hasScans) setIsFullscreen(false);
    }, [hasScans]);

    // Escape is the expected way out of a full-viewport surface.
    useEffect(() => {
        if (!isFullscreen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isFullscreen]);

    const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);

    return { isFullscreen, toggleFullscreen };
}

function MonitoringPanel(props: MonitoringPanelProps) {
    const {
        scans,
        scansLoading = false,
        emptyState,
        className = '',
        sidePanel,
        allowFullscreen = false,
        headerActions,
        detailsMenu
    } = props;
    const [mediaView, setMediaView] = useState<MediaView>('photos');
    const [showSidePanel, setShowSidePanel] = useState(false);
    const hasScans = !!scans && scans.length > 0;
    const { isFullscreen, toggleFullscreen } = useFullscreen(hasScans);

    // Collapsing hands the side panel's width back to the photo.
    const activeSidePanel = sidePanel && showSidePanel ? sidePanel : undefined;

    if (!scansLoading && !hasScans) {
        return (
            <PanelEmptyState
                emptyState={emptyState}
                className={className}
                headerActions={headerActions}
                detailsMenu={detailsMenu}
            />
        );
    }

    return (
        <PanelShell isFullscreen={isFullscreen} className={className}>
            {/* Timeline and header are per-scan; morph and compare are patient-level. */}
            {mediaView === 'photos' && (
                <ScanTimeline
                    scans={props.scans}
                    scansLoading={scansLoading}
                    selectedScanId={props.selectedScanId}
                    selectedScan={props.selectedScan}
                    onSelectScan={props.onSelectScan}
                    totalAligners={props.totalAligners}
                    monitoringStatus={props.monitoringStatus}
                    currentAligner={props.currentAligner}
                    formatScanDate={props.formatScanDate}
                    showTrack={props.showScanSelector ?? true}
                    tracksAligners={props.tracksAligners ?? true}
                    hasSidePanel={!!sidePanel}
                    showSidePanel={showSidePanel}
                    onToggleSidePanel={() => setShowSidePanel((v) => !v)}
                    headerActions={headerActions}
                    detailsMenu={detailsMenu}
                />
            )}
            <MonitoringBody
                {...props}
                sidePanel={activeSidePanel}
                mediaView={mediaView}
                onSelectView={setMediaView}
                onToggleFullscreen={allowFullscreen ? toggleFullscreen : undefined}
                isFullscreen={isFullscreen}
            />
        </PanelShell>
    );
}

export { MonitoringPanel };
